import type { FacetValueFilterInput } from '#graphql-operations'
import type { Ref } from 'vue'

export function getProductFacetValueIds(): string[] {
  const route = useRoute()
  return route.query.pfvid ? (route.query.pfvid as string).split(',') : []
}

export function getProductVariantFacetValueIds(): string[] {
  const route = useRoute()
  return route.query.vfvid ? (route.query.vfvid as string).split(',') : []
}

export function getFacetedSellerId() {
  const route = useRoute()
  return route.name?.toString().indexOf('stores-id') !== -1 ? route.params.id?.toString() : undefined
}

export function useProductsGridQueryParams() {
  const route = useRoute()
  return {
    term: computed(() => route.query.q?.toString()),
  }
}

function useSearchFacetValueFilters(): { facetValueFilters: Ref<FacetValueFilterInput[]> } {
  return {
    facetValueFilters: computed(() => {
      const filters: FacetValueFilterInput[] = []
      const facetedSellerId = getFacetedSellerId()

      const productFacetValueIds = getProductFacetValueIds()
      if (productFacetValueIds.length) {
        filters.push({ or: productFacetValueIds, byProduct: true })
      }

      const productVariantFacetValueIds = getProductVariantFacetValueIds()
      if (productVariantFacetValueIds.length) {
        filters.push({ or: productVariantFacetValueIds, byProduct: false })
      }

      // Add seller ID if applicable
      if (facetedSellerId)
        filters.push({ and: facetedSellerId })

      return filters
    }),
  }
}

function useSearchByCollectionId() {
  const route = useRoute()

  return {
    collectionId: computed(() => {
      return route.name?.toString().indexOf('collections-id') !== -1
        ? route.params.id?.toString()
        : undefined
    }),
  }
}

export function useSearchByProductId() {
  const route = useRoute()

  return {
    productId: computed(() => {
      return route.name?.toString().indexOf('products-id') !== -1
        ? route.params.id?.toString()
        : undefined
    }),
  }
}

function useSearchPriceRange() {
  const route = useRoute()

  const priceRange = computed(() => {
    const minPrice = route.query.min ?? undefined

    const defaultParams = {
      min: minPrice ? Number(minPrice) : 0,
    }

    if (route.query.max) {
      return {
        ...defaultParams,
        max: Number(route.query.max),
      }
    }

    return defaultParams as { min: number, max: number } // temporary typing fix
  })

  return { priceRange: computed(() => priceRange.value) }
}

export function useSearchParams() {
  const { term } = useProductsGridQueryParams()
  const { facetValueFilters } = useSearchFacetValueFilters()
  const { collectionId } = useSearchByCollectionId()
  const { priceRange } = useSearchPriceRange()

  return {
    searchParams: computed(() => ({
      term: term.value,
      facetValueFilters: facetValueFilters.value,
      collectionId: collectionId.value,
      priceRange: priceRange.value,
    })),
  }
}
